import { Button } from '@shared/components/ui/button';
import { Product } from '@shared/components/ui/Product';
import { Search } from '@shared/components/ui/Search/Search';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpArrowDown, faCheck, faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

interface LocatedProduct {
  id: string;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location: string;
  image?: string;
}

interface LocatedProductsSectionProps {
  products: LocatedProduct[];
  onSort: (sortBy: string) => void;
  currentSort: string;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

export const LocatedProductsSection = ({
  products,
  onSort,
  currentSort,
  searchQuery = '',
  onSearchChange,
}: LocatedProductsSectionProps) => {
  const { t } = useTranslation('receiveModule');

  return (
    <div className="w-full md:w-[50%] flex flex-col gap-5 p-4 h-full">
      {/* Header - Productos ubicados */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-title text-lg">{t('ui.located_products')}</h2>
          <span className="inline-flex items-center justify-center rounded-full px-2 bg-bg-info text-info text-sm">
            {products.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Search
            dataTestId="located-search"
            placeholder={t('items.search_placeholder')}
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.currentTarget.value)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8" data-testid="located-sort-button">
                <FontAwesomeIcon icon={faArrowUpArrowDown} className="w-3.5 h-3.5 text-icons" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem
                data-testid="located-sort-more-qty"
                onClick={() => onSort('more_qty')}
                className={currentSort === 'more_qty' ? 'bg-card-border' : ''}
              >
                {t('ui.sort.more_qty')}
                {currentSort === 'more_qty' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="located-sort-less-qty"
                onClick={() => onSort('less_qty')}
                className={currentSort === 'less_qty' ? 'bg-card-border' : ''}
              >
                {t('ui.sort.less_qty')}
                {currentSort === 'less_qty' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="located-sort-name-az"
                onClick={() => onSort('name_az')}
                className={currentSort === 'name_az' ? 'bg-card-border' : ''}
              >
                {t('ui.sort.name_az')}
                {currentSort === 'name_az' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="located-sort-name-za"
                onClick={() => onSort('name_za')}
                className={currentSort === 'name_za' ? 'bg-card-border' : ''}
              >
                {t('ui.sort.name_za')}
                {currentSort === 'name_za' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Contenido */}
      <div className="flex-1">
        {products.length > 0 ? (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {products.map((product) => (
              <div key={product.id} className="flex items-center justify-between py-3 border-b border-card-border">
                {/* Componente Product */}
                <Product
                  id={parseInt(product.id)}
                  src={product.image || '/imagen/example.webp'}
                  name={product.name}
                  code={product.code}
                  className="flex-1"
                />

                {/* Ubicación y cantidad */}
                <div className="flex items-center gap-3">
                  {/* Badge de ubicación con diseño específico */}
                  <div className="inline-flex items-center gap-2 bg-card-border light:bg-badge-bg-status-1 text-badge-status-1 rounded-full px-3 py-1">
                    <FontAwesomeIcon icon={faLocationDot} className="w-3 h-3" />
                    <span className="text-xs font-medium">{product.code}</span>
                  </div>
                  <div className="flex items-center gap-2 bg-card-border light:bg-bg-success rounded-full px-3 py-1">
                    <span className="text-xs text-success">{product.quantity}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center h-full mt-4 sm:mt-0">
            <div className="text-center text-complementary-text text-sm max-w-60">
              {searchQuery && searchQuery.trim() ? t('items.search.no_results.title') : t('ui.no_located_products')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
